/**
 * Reset Password Modal
 * Handles password reset flow when user clicks the link from their email
 * URL format: #resetpassword~{GUID}
 */

import { ToasterService } from '@/services/ToasterService';
import { ModalSystem } from '@/services/ModalSystem';
import { loadOEMTemplate } from '@/services/OEMUtils';
import { Logger } from '@/services/Logger';
import { getSiteKey } from '@/services/api/TriSysWebAPI';

const logger = Logger.scope('ResetPassword');

/**
 * Check if the current URL hash contains a reset password token
 * Returns the GUID if found, null otherwise
 */
export function getResetPasswordGUID(): string | null {
  const hash = window.location.hash;
  if (!hash || !hash.startsWith('#resetpassword~')) return null;
  const guid = hash.substring('#resetpassword~'.length);
  if (!guid || guid.length < 10) return null;
  return guid;
}

/**
 * Handle the reset password flow
 * Validates the GUID, retrieves the email, then shows the reset form
 */
export async function handleResetPassword(guid: string): Promise<void> {
  const apiBase = sessionStorage.getItem('trisys_api_endpoint') || 'https://api.trisys.co.uk';

  // Step 1: Validate the GUID and get the email address
  logger.debug('Validating reset password GUID', { guid });

  let email: string;
  try {
    const response = await fetch(`${apiBase}/Security/PasswordResetInvocation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SiteKey': getSiteKey()
      },
      body: JSON.stringify({ GUID: guid })
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const result = await response.json();

    if (!result.Success) {
      ToasterService.error(result.ErrorMessage || 'This reset link is invalid or has already been used.');
      // Clear the hash so the normal app flow can continue
      window.location.hash = '';
      return;
    }

    email = result.EMail;
    if (!email) {
      ToasterService.error('Unable to retrieve account details. Please request a new reset link.');
      window.location.hash = '';
      return;
    }

    logger.debug('GUID validated, email retrieved', { email });
  } catch (error) {
    logger.error('Failed to validate reset GUID:', error);
    ToasterService.error('Failed to verify reset link. Please try again.');
    window.location.hash = '';
    return;
  }

  // Step 2: Show the reset password modal
  try {
    const html = await loadOEMTemplate('/forms/auth/reset-password.html');

    ModalSystem.show({
      title: 'Reset Password',
      html,
      width: 'lg',
      preventClose: true,
      onOpened: () => {
        setupResetPasswordForm(guid, email, apiBase);
      }
    });
  } catch (error) {
    logger.error('Error loading reset password form:', error);
    ToasterService.error('Failed to load reset password form');
    window.location.hash = '';
  }
}

/**
 * Setup the reset password form
 */
function setupResetPasswordForm(guid: string, email: string, apiBase: string): void {
  const form = document.getElementById('reset-password-form') as HTMLFormElement;
  if (!form) {
    logger.warn('Reset password form not found');
    return;
  }

  // Populate the read-only email field
  const emailInput = document.getElementById('reset-email') as HTMLInputElement;
  if (emailInput) {
    emailInput.value = email;
  }

  // Focus the password field
  const passwordInput = document.getElementById('reset-password') as HTMLInputElement;
  passwordInput?.focus();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const password = (document.getElementById('reset-password') as HTMLInputElement)?.value;
    const confirmPassword = (document.getElementById('reset-confirm-password') as HTMLInputElement)?.value;

    // Validate
    if (!password || !confirmPassword) {
      ToasterService.warning('Please fill in both password fields');
      return;
    }

    if (password.length < 6) {
      ToasterService.warning('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      ToasterService.warning('Passwords do not match');
      return;
    }

    const submitBtn = document.getElementById('reset-save-btn') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Saving...';

    try {
      logger.debug('Saving new password', { email });

      const response = await fetch(`${apiBase}/security/PasswordResetSubmission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'SiteKey': getSiteKey()
        },
        body: JSON.stringify({
          GUID: guid,
          EMail: email,
          Password: password,
          Product: 'BSG API Hub'
        })
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const result = await response.json();

      if (!result.Success) {
        ToasterService.error(result.ErrorMessage || 'Failed to reset password. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
        return;
      }

      ToasterService.success('Password has been reset successfully. You can now sign in.');
      ModalSystem.hide();
      // Clear the hash and reload to show login screen
      window.location.hash = '';
      window.location.reload();
    } catch (error) {
      logger.error('Failed to reset password:', error);
      ToasterService.error('Failed to reset password. Please try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}
